import { motion } from 'framer-motion';
import { staggeredFadeIn } from '../../utils/animations';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const faqs = [
    {
        q: 'What exactly is White Glove Hiring?',
        a: "White Glove Hiring is a VA hiring and operational partnership program for business owners. We handle the full hiring process - role design, candidate sourcing, skills testing, structured interviews, and offer guidance - and then stay involved through structured onboarding, monthly timeclock audits, dedicated support, and an annual business review. It is one program, not a staffing directory.",
    },
    {
        q: 'How much does it cost?',
        a: "The program starts at $900/month in Year 1, drops to $699/month in Year 2, and $299/month in Year 3 and beyond. This covers the full hiring process plus ongoing oversight for one VA placement. VA wages are paid directly by you - we do not mark up labor. At a typical rate of $10/hour with 160 hours per month, the all-in cost starts at approximately $15.63/hour and decreases to $11.87/hour over time. VA talent typically ranges from $8 to $12/hour or more depending on skill set and specialty.",
    },
    {
        q: "What's included in the monthly fee?",
        a: "Everything. Precision hiring system (role design, skills testing, behavioral screening, structured interviews, offer guidance), structured onboarding roadmap, a dedicated Customer Success Representative available via Slack, phone, or text, monthly timeclock audits for placed VAs, an annual business review call, and the free replacement guarantee. No hidden fees.",
    },
    {
        q: 'What is the minimum commitment?',
        a: "The minimum engagement is 3 months. We keep the entry bar low because we are confident in what we deliver. After 3 months, the program continues month-to-month with 30 days written notice. If you wish to keep your VA and end the program, a buyout option is available: the buyout is 18 months of your current monthly program fee. Because that fee decreases over time, so does your buyout.",
    },
    {
        q: "What if the VA doesn't work out?",
        a: "We replace them. No restart fee, no additional recruiting charge. That is the standard. Our process significantly reduces the probability of a failed placement, but if it happens, we do not make you start over or pay again.",
    },
    {
        q: 'What does the monthly timeclock audit involve?',
        a: "Every month we review your VA's time logs in detail - looking for inefficiency, drift, scope creep, and accountability gaps. We flag coaching opportunities and provide structured feedback recommendations. This is proactive oversight, not reactive damage control. You receive a summary of findings and any recommended actions.",
    },
    {
        q: 'Can I add more VAs later?',
        a: "Yes. After your first hire is placed, each additional VA is $699/month for their first 12 months, then $299/month. Each additional VA follows their own independent timeline from their hire date, and goes through the same hiring rigor, onboarding support, and monthly oversight as your first. There is also a $100/month per VA option for monitoring VAs you already have on your team.",
    },
    {
        q: 'What types of businesses do you work with?',
        a: "We work primarily with business owners in real estate, professional services, marketing agencies, consulting, and similar fields. If you are serious about building real operational leverage and are tired of bad VA experiences, we are likely a fit. The consultation call is the right place to confirm.",
    },
    {
        q: 'Do you work with businesses outside the US?',
        a: "Our program is built for US-based business owners. The VAs we place are typically remote and may be located internationally. The program structure - hiring process, oversight, and support - is designed for a North American business context.",
    },
    {
        q: 'How do I get started?',
        a: "Fill out the contact form or schedule a free consultation call. We will ask about your business, your current team, and what you are looking to build. From there, if it is a fit, we move into the program.",
    },
];

export function FAQAccordion() {
    return (
        <section className="bg-white py-24 md:py-32">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
                <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
                    {faqs.map((faq, i) => (
                        <motion.div
                            key={faq.q}
                            {...staggeredFadeIn(i * 0.05)}
                            data-testid={`faq-item-${i}`}
                        >
                            <AccordionItem value={`item-${i}`} className="border border-gray-100 rounded-sm overflow-hidden bg-white shadow-sm">
                                <AccordionTrigger className="px-7 py-5 text-left font-montserrat font-bold text-navy-900 text-sm hover:text-gold-500 hover:no-underline transition-colors [&[data-state=open]]:text-gold-500">
                                    {faq.q}
                                </AccordionTrigger>
                                <AccordionContent className="px-7 pb-6 text-[#6B7280] text-sm leading-relaxed">
                                    {faq.a}
                                </AccordionContent>
                            </AccordionItem>
                        </motion.div>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}
